import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';

// 创建 axios 实例
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  timeout: 30000, // 增加到30秒，避免大数据量请求超时
  headers: {
    'Content-Type': 'application/json',
  },
});

// 独立的 auth client：避免被拦截器递归影响
const authClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// 生成/复用相关：X-Request-ID（用于后端 correlation_id 聚合）
const BATCH_WINDOW_MS = 15000; // 15s 内的请求视为同一批（除非外部显式覆写）
function genUUID(): string {
  try {
    // 现代浏览器
    // @ts-ignore
    if (typeof crypto !== 'undefined' && crypto?.randomUUID) return crypto.randomUUID();
  } catch {}
  // 兼容实现
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
function getOrCreateBatchRequestId(): string {
  try {
    const keyId = 'X_REQ_ID_CURRENT';
    const keyTs = 'X_REQ_ID_EXPIRES';
    const now = Date.now();
    const cur = sessionStorage.getItem(keyId);
    const exp = Number(sessionStorage.getItem(keyTs) || '0');
    if (cur && exp && now < exp) return cur;
    const nid = genUUID();
    sessionStorage.setItem(keyId, nid);
    sessionStorage.setItem(keyTs, String(now + BATCH_WINDOW_MS));
    return nid;
  } catch {
    // sessionStorage 不可用时直接生成一次性
    return genUUID();
  }
}

// 请求拦截器
instance.interceptors.request.use(
  (config) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 注入 X-Request-ID（若外部未显式设置）
    try {
      const headers: any = config.headers || {};
      const existing = headers['X-Request-ID'] || headers['x-request-id'];
      const url = String(config.url || '');
      // 兼容CORS：鉴权相关接口先不注入该头，避免未更新后端CORS时登陆受阻
      const isAuth = /\/api\/v1\/auth\/(login|logout)\//.test(url) || /\/api\/v1\/auth\/token\/refresh\//.test(url);
      if (!existing && !isAuth) {
        headers['X-Request-ID'] = getOrCreateBatchRequestId();
      }
      config.headers = headers;
    } catch {}
    
    // 请求数据已处理
    
    return config;
  },
  (error) => {
    console.error('❌ [Request Interceptor] 请求错误:', error);
    return Promise.reject(error);
  }
);

type RefreshResp = { access: string; refresh?: string };
let _refreshing: Promise<string> | null = null;
async function refreshAccessToken(): Promise<string> {
  // 单飞：避免并发 401 时重复 refresh
  if (_refreshing) return _refreshing;
  _refreshing = (async () => {
    const rt = localStorage.getItem('refresh_token') || '';
    if (!rt) throw new Error('missing refresh_token');
    const resp = await authClient.post<RefreshResp>('/api/v1/auth/token/refresh/', { refresh: rt });
    const access = (resp.data as any)?.access;
    const newRefresh = (resp.data as any)?.refresh;
    if (!access) throw new Error('refresh response missing access');
    localStorage.setItem('token', String(access));
    if (newRefresh) {
      try { localStorage.setItem('refresh_token', String(newRefresh)); } catch {}
    }
    return String(access);
  })();
  try {
    return await _refreshing;
  } finally {
    _refreshing = null;
  }
}

// 响应拦截器
instance.interceptors.response.use(
  (response) => {
    // 响应数据已处理
    return response;
  },
  async (error) => {
    console.error('❌ [Response Interceptor] 响应错误:', error);
    console.error('❌ [Response Interceptor] 错误类型:', error.constructor.name);
    console.error('❌ [Response Interceptor] 错误消息:', error.message);
    
    if (error.response) {
      console.error('❌ [Response Interceptor] HTTP响应状态:', error.response.status);
      console.error('❌ [Response Interceptor] HTTP响应数据:', error.response.data);
    } else if (error.request) {
      console.error('❌ [Response Interceptor] 请求已发送但无响应');
    } else {
      console.error('❌ [Response Interceptor] 请求配置错误');
    }
    
    const getErrorCode = async (): Promise<string> => {
      try {
        const data = error.response?.data;
        // 常规 JSON 错误体
        if (data && typeof data === 'object' && !(data instanceof Blob) && !(data instanceof ArrayBuffer)) {
          return String((data as any)?.code || '').trim();
        }
        // responseType=blob 时，后端错误体也可能是 JSON Blob
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            return String(parsed?.code || '').trim();
          } catch {
            return '';
          }
        }
        // responseType=arraybuffer 的兜底
        if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
          const text = new TextDecoder('utf-8').decode(data);
          try {
            const parsed = JSON.parse(text);
            return String(parsed?.code || '').trim();
          } catch {
            return '';
          }
        }
      } catch {}
      return '';
    };
    const errCode = await getErrorCode();
    const isTokenInvalid403 = error.response?.status === 403 && errCode === 'token_not_valid';
    const requestUrl = String(error?.config?.url || '');
    const isLoginRequest = /\/api\/v1\/auth\/login\/?$/.test(requestUrl);
    if (isLoginRequest) {
      // 登录失败时应将后端错误直接返回给页面，避免被全局401处理覆盖
      return Promise.reject(error);
    }
    if (error.response?.status === 401 || isTokenInvalid403) {
      // 401 / 403(token_not_valid)：优先尝试静默续期（保持登录状态），仅在续期失败时才跳转登录
      const originalConfig: any = error.config || {};
      if (!originalConfig || originalConfig.__isRetryRequest) {
        try { localStorage.removeItem('token'); } catch {}
        try { localStorage.removeItem('refresh_token'); } catch {}
        window.location.href = '/login';
        return Promise.reject(error);
      }
      originalConfig.__isRetryRequest = true;
      return refreshAccessToken()
        .then((newAccess) => {
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers.Authorization = `Bearer ${newAccess}`;
          return instance.request(originalConfig);
        })
        .catch((e) => {
          try { console.error('[Auth][Refresh] failed:', e); } catch {}
          try { localStorage.removeItem('token'); } catch {}
          try { localStorage.removeItem('refresh_token'); } catch {}
          window.location.href = '/login';
          return Promise.reject(error);
        });
    }
    return Promise.reject(error);
  }
);

// 封装请求方法
const request = {
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return instance.get<T>(url, config);
  },

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return instance.post<T>(url, data, config);
  },

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return instance.put<T>(url, data, config);
  },

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return instance.delete<T>(url, config);
  },

  async request<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return instance.request<T>(config);
  },

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return instance.patch<T>(url, data, config);
  }
};

export default request; 